import React, { useState, useEffect, useRef } from 'react';
import { Upload, Download, Trash2, File } from 'lucide-react';
import api from '../utils/api';
import { fmtDate } from '../utils/helpers';
import { PageHeader, DataTable, SearchBar, Modal, FormField } from '../components/Table';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { downloadExport } from '../utils/exportUtils';

const CATEGORIES = ['PAN', 'Aadhaar', 'ITR Copies', 'Acknowledgements', 'Computation', 'GST Returns', 'TDS Returns', 'Audit Reports', 'Balance Sheet', 'ROC Forms', 'Notices', 'Replies', 'Challans', 'Others'];

export default function DocumentsPage() {
  const [sp] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [clientFilter, setClientFilter] = useState(sp.get('client') || '');
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ category: 'Others', ay_fy: '' });
  const fileRef = useRef(null);

  const load = () => {
    const params = {};
    if (clientFilter) params.client_id = clientFilter;
    if (catFilter) params.category = catFilter;
    api.get('/documents', { params }).then(r => setDocs(r.data));
  };

  useEffect(() => { api.get('/clients', { params: { status: 'Active' } }).then(r => setClients(r.data)); }, []);
  useEffect(() => { load(); }, [clientFilter, catFilter]);

  const handleUpload = async e => {
    e.preventDefault();
    if (!fileRef.current?.files[0]) { toast.error('Select a file'); return; }
    if (!form.client_id) { toast.error('Select a client'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileRef.current.files[0]);
      fd.append('client_id', form.client_id);
      fd.append('category', form.category);
      fd.append('ay_fy', form.ay_fy || '');
      fd.append('notes', form.notes || '');
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded'); setShowModal(false); setForm({ category: 'Others', ay_fy: '' }); load();
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this document?')) return;
    await api.delete(`/documents/${id}`);
    toast.success('Deleted'); load();
  };

  const filtered = docs.filter(d => !search || d.original_name?.toLowerCase().includes(search.toLowerCase()) || d.client_name?.toLowerCase().includes(search.toLowerCase()));

  const columns = [
    { key: 'original_name', label: 'File Name', render: v => (
      <div className="flex items-center gap-2"><File size={14} className="text-gray-400" /><span className="font-medium text-gray-700">{v}</span></div>
    )},
    { key: 'client_name', label: 'Client' },
    { key: 'category', label: 'Category', render: v => <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{v}</span> },
    { key: 'ay_fy', label: 'AY/FY' },
    { key: 'uploader_name', label: 'Uploaded By' },
    { key: 'uploaded_at', label: 'Date', render: v => fmtDate(v) },
    { key: 'size', label: 'Size', render: v => v ? `${(v / 1024).toFixed(0)} KB` : '—' },
    { key: 'id', label: '', sortable: false, render: (v, row) => (
      <div className="flex gap-2">
        <button onClick={() => downloadExport(`/documents/download/${row.filename}`, {}, row.original_name)} className="text-gray-400 hover:text-[#1e3a5f]" title="Download"><Download size={15} /></button>
        <button onClick={() => handleDelete(v)} className="text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="Document Vault" subtitle={`${filtered.length} documents`}
        actions={<button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2"><Upload size={16} /> Upload Document</button>} />

      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4">
          <SearchBar value={search} onChange={setSearch} placeholder="Search documents..." />
          <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} className="input-field w-48">
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.client_code ? c.client_code + ' — ' + c.name : c.name}</option>)}
          </select>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="input-field w-40">
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <DataTable columns={columns} data={filtered} emptyMessage="No documents. Upload your first document." />
      </div>

      {showModal && (
        <Modal title="Upload Document" onClose={() => setShowModal(false)}>
          <form onSubmit={handleUpload} className="space-y-4">
            <FormField label="Client" required>
              <ClientSearchDropdown
                value={form.client_id || ''}
                initialLabel=""
                onChange={(id) => setForm(f => ({ ...f, client_id: id }))}
                required
              />
            </FormField>
            <FormField label="Category">
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="input-field">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="AY / FY Tag"><input value={form.ay_fy||''} onChange={e => setForm(f => ({...f, ay_fy: e.target.value}))} className="input-field" placeholder="e.g. AY 2026-27" /></FormField>
            <FormField label="File" required>
              <input type="file" ref={fileRef} className="input-field" accept=".pdf,.xlsx,.xls,.doc,.docx,.jpg,.png,.zip" />
            </FormField>
            <FormField label="Notes"><textarea value={form.notes||''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3">
              <button type="submit" disabled={uploading} className="btn-primary flex-1">{uploading ? 'Uploading...' : 'Upload'}</button>
              <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
